// PermissionProfilePage.js
import { observer } from 'mobx-react-lite';
import Layout from '../Layout/Layout';
import { useEffect, useState, useCallback } from 'react';
import {
    Box, Typography, Snackbar, Alert, CircularProgress, Button,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { parseQueryParams, updateUrlParams } from '../../Utils/urlUtils';
import PermissionProfileFilterPanel from './PermissionProfileFilterPanel';
import PermissionProfileTable from './PermissionProfileTable';
import AddPermissionProfile from './AddPermissionProfile';
import UpdatePermissionProfile from './UpdatePermissionProfile';
import permissionProfileStore from '../../Mobx/PermissionProfileStore';

const PermissionProfilePage = observer(() => {
    const location = useLocation();

    // --- parse from URL ---
    const {
        filters: initialFilters,
        sortField: initialSortField,
        sortOrder: initialSortOrder,
        page: initialPage,
        limit: initialLimit
    } = parseQueryParams(new URLSearchParams(location.search), ['name'], 'createdAt');

    // --- useStates based on URL + localStorage ---
    const LIMIT_KEY = 'limitPermissionProfilePage';
    const [filters, setFilters] = useState(initialFilters);
    const [sortField, setSortField] = useState(initialSortField);
    const [sortOrder, setSortOrder] = useState(initialSortOrder);
    const [page, setPage] = useState(initialPage);
    const [limit, setLimit] = useState(() => {
        const stored = localStorage.getItem(LIMIT_KEY);
        return stored ? Number(stored) : initialLimit;
    });

    useEffect(() => {
        localStorage.setItem(LIMIT_KEY, limit);
    }, [limit]);

    // --- dialog + snackbar states ---
    const [isAddOpen, setIsAddOpen] = useState(false);
    const [editProfile, setEditProfile] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

    const showSnackbar = (message, severity = 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    // --- fetch profiles with error handling ---
    const fetchProfiles = useCallback(async () => {
        try {
            await permissionProfileStore.fetchProfiles({
                page,
                limit,
                filters,
                sort: [{ field: sortField, order: sortOrder }]
            });
        } catch (err) {
            showSnackbar(
                <>
                    שגיאה בטעינת פרופילים:<br />
                    {err?.response?.data?.error || err.toString()}
                </>
            );
        }
    }, [page, limit, filters, sortField, sortOrder]);

    // --- update URL + fetch ---
    useEffect(() => {
        updateUrlParams({ filters, sortField, sortOrder, page, limit, dateKey: 'createdAt' });
        fetchProfiles();
    }, [filters, sortField, sortOrder, page, limit, fetchProfiles]);

    // --- delete profile ---
    const handleDelete = (profile) => {
        setDeleteTarget(profile);
        setIsConfirmOpen(true);
    };

    const confirmDelete = async () => {
        try {
            await permissionProfileStore.deleteProfile(deleteTarget._id);
            setIsConfirmOpen(false);
            setDeleteTarget(null);
            fetchProfiles();
        } catch (err) {
            showSnackbar(
                <>
                    שגיאה במחיקת פרופיל:<br />
                    {err?.response?.data?.error || err.toString().replace(/&quot;/g, '"')}
                </>
            );
        }
    };

    // --- top right tools ---
    const tools = [
        <Box
            key="total-profiles"
            sx={{
                backgroundColor: 'white', px: 2, py: 1, borderRadius: 2, boxShadow: 1,
                display: 'inline-block', fontWeight: 'bold', color: 'primary.main', border: '1px solid #ccc',
                minWidth: '220px', textAlign: 'center'
            }}>
            <Typography variant="body2">מספר פרופילים שנמצאו: {permissionProfileStore.total}</Typography>
        </Box>,
        <Button key="add-button" variant="contained" color="primary" onClick={() => setIsAddOpen(true)}>
            הוספת פרופיל הרשאות
        </Button>
    ];

    // --- drawer filters ---
    const drawerTools = [
        <PermissionProfileFilterPanel
            key="filter-panel"
            filters={filters}
            setFilters={setFilters}
            sortField={sortField}
            setSortField={setSortField}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            fetchProfiles={fetchProfiles}
            setPage={setPage}
            limit={limit}
            setLimit={setLimit}
        />
    ];

    return (
        <div>
            <Layout
                tools={tools}
                drawerTools={drawerTools}
                toolsConfig={{ toolsHeight: 45, toolsBackgroundColor: '#FFFFFF', drawerToolsWidth: 400 }}
            >
                {permissionProfileStore.isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: `calc(100vh - 95px)` }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box sx={{ height: `calc(100vh - 95px)`, overflowY: 'auto', flexGrow: 1 }}>
                        <PermissionProfileTable
                            profiles={permissionProfileStore.profiles}
                            total={permissionProfileStore.total}
                            page={page}
                            limit={limit}
                            setPage={setPage}
                            onEdit={setEditProfile}
                            onDelete={handleDelete}
                        />
                    </Box>
                )}
            </Layout>

            <AddPermissionProfile open={isAddOpen} onClose={() => setIsAddOpen(false)} onSuccess={fetchProfiles} />
            <UpdatePermissionProfile profile={editProfile} onClose={() => setEditProfile(null)} onSuccess={fetchProfiles} />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={null}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <Dialog open={isConfirmOpen} onClose={() => setIsConfirmOpen(false)}>
                <DialogTitle>אישור מחיקה</DialogTitle>
                <DialogContent>
                    <Typography>האם אתה בטוח שברצונך למחוק את הפרופיל "{deleteTarget?.name}"?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsConfirmOpen(false)}>ביטול</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">מחק</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
});

export default PermissionProfilePage;
